// Platform-agnostic storage interface.
// Web implementation: localStorage adapter
// Mobile implementation: AsyncStorage adapter

export interface IStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

/** Wraps a raw value (sync or async) in a Promise. */
async function resolve<T>(value: T | Promise<T>): Promise<T> {
  return value;
}

const KEYS = {
  deviceId: 'auth_device_id',
  token: 'auth_token',
  refreshToken: 'auth_refresh_token',
  tokenExpiry: 'auth_token_expiry',
  username: 'auth_username',
  emailConfirmPending: 'auth_email_confirm_pending',
} as const;

export interface AuthResponse {
  token: string;
  refreshToken: string;
  tokenExpiry: string; // ISO-8601
  username?: string;
}

export interface AuthServiceConfig {
  authBaseUrl: string;
  subscriptionKey?: string;
  storage: IStorage;
}

function generateGuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function createAuthService(config: AuthServiceConfig) {
  const { storage } = config;
  const AUTH_BASE = config.authBaseUrl.replace(/\/$/, '');
  const SUBSCRIPTION_KEY = config.subscriptionKey ?? '';

  function commonHeaders(extra: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = { ...extra };
    if (SUBSCRIPTION_KEY) headers['Ocp-Apim-Subscription-Key'] = SUBSCRIPTION_KEY;
    return headers;
  }

  async function getOrCreateDeviceId(): Promise<string> {
    let id = await resolve(storage.getItem(KEYS.deviceId));
    if (!id) {
      id = generateGuid();
      await resolve(storage.setItem(KEYS.deviceId, id));
    }
    return id;
  }

  async function getStoredToken(): Promise<string | null> {
    return resolve(storage.getItem(KEYS.token));
  }

  async function getStoredRefreshToken(): Promise<string | null> {
    return resolve(storage.getItem(KEYS.refreshToken));
  }

  async function getStoredUsername(): Promise<string | null> {
    return resolve(storage.getItem(KEYS.username));
  }

  async function isTokenExpired(): Promise<boolean> {
    const expiry = await resolve(storage.getItem(KEYS.tokenExpiry));
    if (!expiry) return true;
    return new Date(expiry) <= new Date(Date.now() + 30_000);
  }

  async function storeAuthResponse(auth: AuthResponse): Promise<void> {
    await Promise.all([
      resolve(storage.setItem(KEYS.token, auth.token)),
      resolve(storage.setItem(KEYS.refreshToken, auth.refreshToken)),
      resolve(storage.setItem(KEYS.tokenExpiry, auth.tokenExpiry)),
      auth.username
        ? resolve(storage.setItem(KEYS.username, auth.username))
        : Promise.resolve(),
    ]);
  }

  async function clearAuth(): Promise<void> {
    await Promise.all(
      Object.values(KEYS).map((k) => resolve(storage.removeItem(k)))
    );
  }

  async function register(
    username: string,
    email: string,
    password: string
  ): Promise<AuthResponse> {
    const deviceId = await getOrCreateDeviceId();
    const res = await fetch(`${AUTH_BASE}/accounts/register`, {
      method: 'POST',
      headers: commonHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ username, email, password, deviceId }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Register failed ${res.status}${text ? `: ${text}` : ''}`);
    }
    const auth = await (res.json() as Promise<AuthResponse>);
    await storeAuthResponse(auth);
    return auth;
  }

  async function login(username: string, password: string): Promise<AuthResponse> {
    const deviceId = await getOrCreateDeviceId();
    const res = await fetch(`${AUTH_BASE}/accounts/login`, {
      method: 'POST',
      headers: commonHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ username, password, deviceId }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Login failed ${res.status}${text ? `: ${text}` : ''}`);
    }
    const auth = await (res.json() as Promise<AuthResponse>);
    await storeAuthResponse(auth);
    return auth;
  }

  // A single in-flight promise is reused so concurrent requests don't each
  // independently kick off a refresh call.
  let pendingRefresh: Promise<AuthResponse> | null = null;

  function silentRefresh(): Promise<AuthResponse> {
    if (pendingRefresh) return pendingRefresh;

    const refresh = doSilentRefresh();
    pendingRefresh = refresh;
    const reset = () => {
      pendingRefresh = null;
    };
    refresh.then(reset, reset);
    return refresh;
  }

  async function doSilentRefresh(): Promise<AuthResponse> {
    const [accessToken, refreshToken, deviceId] = await Promise.all([
      getStoredToken(),
      getStoredRefreshToken(),
      getOrCreateDeviceId(),
    ]);
    if (!accessToken || !refreshToken) {
      throw new Error('No credentials available for token refresh');
    }
    const res = await fetch(`${AUTH_BASE}/accounts/refresh-token`, {
      method: 'POST',
      headers: commonHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ accessToken, refreshToken, deviceId }),
    });
    if (!res.ok) {
      await clearAuth();
      throw new Error(`Refresh failed ${res.status}`);
    }
    const auth = await (res.json() as Promise<AuthResponse>);
    await storeAuthResponse(auth);
    return auth;
  }

  async function confirmAccount(username: string, code: string): Promise<void> {
    const url = `${AUTH_BASE}/accounts/confirm-account?username=${encodeURIComponent(username)}&code=${encodeURIComponent(code)}`;
    const res = await fetch(url, { method: 'POST', headers: commonHeaders() });
    if (!res.ok) throw new Error(`Account confirmation failed ${res.status}`);
  }

  return {
    getStoredToken,
    getStoredRefreshToken,
    getStoredUsername,
    isTokenExpired,
    storeAuthResponse,
    clearAuth,
    register,
    login,
    silentRefresh,
    confirmAccount,
  };
}

export type AuthService = ReturnType<typeof createAuthService>;
